import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Compass, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { STEPS, guideFull, hasGuide, totalMin } from "@/lib/ownerGuide";

// 306: upozornění, že průvodce nastavením není dokončený — průvodce zrovna neběží
// (odložený „Teď ne“ nebo „Základy máš“) a certifikát ještě nemá. Klik otevře přehled průvodce.
// Počítač a tablet: karta na Přehledu. Telefon: tečka u avataru a řádek nahoře v Můj profil.
// 308: i ostatní role (jejich průvodce aplikací), majitel s textem o nastavení firmy.
export const openGuide = () => window.dispatchEvent(new Event("pp-guide-start"));

const HIDE_KEY = "pp-guide-nudge-hide";
const hiddenNow = () => { try { return Number(localStorage.getItem(HIDE_KEY) || 0) > Date.now(); } catch { return false; } };
const hideFor = (ms) => { try { localStorage.setItem(HIDE_KEY, String(Date.now() + ms)); } catch { /* bez paměti */ } };

export function useGuideLeft() {
  const { user } = useAuth();
  const on = hasGuide(user?.role);
  const mobile = useIsMobile();
  const { data: ob } = useQuery({ queryKey: ["onboarding"], queryFn: () => api.get("/onboarding").then((r) => r.data), enabled: on, staleTime: 0 });
  // 395: účet z doby před průvodcem — průvodce se mu nikdy nenabídl (stav chybí); pozvánka místo „X z N hotovo“
  if (on && ob && !ob.cert && ob.state == null) return { legacy: true, mobile, owner: user.role === "OWNER", firm: ["OWNER", "ASSISTANT", "MANAGER"].includes(user.role) };
  if (!on || !ob || ob.cert || !["dismissed", "basics"].includes(ob.state)) return null;
  const full = guideFull(user.role, !mobile, ob.size);
  const left = full.filter((k) => !ob.done?.[k]);
  if (!left.length) return null;
  return { done: full.length - left.length, total: full.length, left, next: left[0], min: totalMin(left), mobile, owner: user.role === "OWNER" };
}

function GuideRing({ done, total, size = 36, stroke = 3.5 }) {
  const r = (size - stroke) / 2, C = 2 * Math.PI * r;
  return (
    <span className="relative shrink-0 grid place-items-center" style={{ width: size, height: size }} aria-hidden="true">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--accent-orange))" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${(C * done) / total} ${C}`} />
      </svg>
      <span className="absolute text-[10px] font-extrabold tabular-nums">{done}/{total}</span>
    </span>
  );
}

// Telefon: tečka u avataru vlevo nahoře (avatar vede do Můj profil, kde je řádek níže)
export function GuideDot() {
  const g = useGuideLeft();
  if (!g?.mobile) return null;
  return <span aria-label={g.owner ? "Nastavení není dokončené" : "Průvodce není dokončený"} data-testid="guide-dot"
    className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-brand ring-2 ring-card pointer-events-none" />;
}

// 395: pozvánka pro účty z doby před průvodcem (text od Petra); workflow jako příklad jen u vedení firmy
const LEGACY_T = "Systém už používáš nějakou dobu. Jsi v něm expert?";
const legacyD = (g) => `Jestli si někde nevíš rady, projdi si průvodce. Třeba objevíš funkce, které ti ušetří mraky času${g.firm ? ", například workflow úkolů" : ""}.`;
function GuideBadge({ size }) {
  return <span className="shrink-0 rounded-full bg-brand/10 text-brand grid place-items-center" style={{ width: size, height: size }} aria-hidden="true"><Compass size={Math.round(size * 0.46)} /></span>;
}

// Telefon: řádek nahoře v Můj profil
export function GuideRow() {
  const g = useGuideLeft();
  if (!g?.mobile) return null;
  if (g.legacy) return (
    <button type="button" onClick={openGuide} data-testid="guide-row"
      className="w-full flex items-center gap-3.5 rounded-[20px] bg-card px-4 py-3.5 text-left active:bg-secondary">
      <GuideBadge size={42} />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-bold leading-snug">{LEGACY_T}</span>
        <span className="block text-[13.5px] text-muted-foreground mt-0.5">{legacyD(g)}</span>
        <span className="block text-[14px] font-bold text-brand mt-1.5">Projít průvodce</span>
      </span>
      <ChevronRight size={18} className="text-muted-foreground shrink-0" />
    </button>
  );
  return (
    <button type="button" onClick={openGuide} data-testid="guide-row"
      className="w-full flex items-center gap-3.5 rounded-[20px] bg-card px-4 py-3.5 text-left active:bg-secondary">
      <GuideRing done={g.done} total={g.total} size={42} />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-bold">{g.owner ? "Dokončete nastavení" : "Dokonči průvodce aplikací"}</span>
        <span className="block text-[13.5px] text-muted-foreground truncate">Další: {STEPS[g.next]?.t} · zbývá asi {g.min} min</span>
      </span>
      <ChevronRight size={18} className="text-muted-foreground shrink-0" />
    </button>
  );
}

// Počítač a tablet: karta na Přehledu nad pruhem, dá se skrýt na týden
export function GuideCard() {
  const g = useGuideLeft();
  const [hidden, setHidden] = useState(hiddenNow);
  if (!g || g.mobile || hidden) return null;
  const later = () => { hideFor(7 * 864e5); setHidden(true); };
  if (g.legacy) return (
    <section data-testid="guide-card" aria-label={g.owner ? "Průvodce nastavením" : "Průvodce aplikací"}
      className="flex items-center gap-4 rounded-[22px] bg-card border border-border px-5 py-4 shadow-[0_1px_2px_rgba(20,24,33,.05)]">
      <GuideBadge size={48} />
      <div className="min-w-0 flex-1">
        <div className="text-[17px] font-bold leading-tight">{LEGACY_T}</div>
        <div className="text-[13.5px] text-muted-foreground mt-0.5 line-clamp-2">{legacyD(g)}</div>
      </div>
      <button type="button" onClick={openGuide} data-testid="guide-card-go"
        className="h-11 px-5 rounded-full bg-brand text-white font-bold hover:bg-brand/90 shrink-0 whitespace-nowrap">Projít průvodce</button>
      <button type="button" onClick={later} data-testid="guide-card-later"
        className="h-11 px-3 rounded-full text-[14px] font-semibold text-muted-foreground hover:text-foreground shrink-0 hidden lg:inline-flex items-center">Skrýt na týden</button>
      <button type="button" onClick={later} aria-label="Skrýt na týden" title="Skrýt na týden"
        className="lg:hidden w-9 h-9 -mr-1 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0"><X size={17} /></button>
    </section>
  );
  return (
    <section data-testid="guide-card" aria-label={g.owner ? "Průvodce nastavením" : "Průvodce aplikací"}
      className="flex items-center gap-4 rounded-[22px] bg-card border border-border px-5 py-4 shadow-[0_1px_2px_rgba(20,24,33,.05)]">
      <GuideRing done={g.done} total={g.total} size={48} stroke={4} />
      <div className="min-w-0 flex-1">
        <div className="text-[17px] font-bold leading-tight">{g.owner ? "Nastavení firmy" : "Průvodce aplikací"}: {g.done} z {g.total} hotovo</div>
        <div className="text-[13.5px] text-muted-foreground mt-0.5 truncate">Další krok: <b className="font-semibold text-foreground">{STEPS[g.next]?.t}</b> · zbývá asi {g.min} min</div>
      </div>
      <button type="button" onClick={openGuide} data-testid="guide-card-go"
        className="h-11 px-5 rounded-full bg-brand text-white font-bold hover:bg-brand/90 shrink-0">Pokračovat</button>
      <button type="button" onClick={later} data-testid="guide-card-later"
        className="h-11 px-3 rounded-full text-[14px] font-semibold text-muted-foreground hover:text-foreground shrink-0 hidden lg:inline-flex items-center">Skrýt na týden</button>
      <button type="button" onClick={later} aria-label="Skrýt na týden" title="Skrýt na týden"
        className="lg:hidden w-9 h-9 -mr-1 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0"><X size={17} /></button>
    </section>
  );
}
